// frontend/src/components/project-input-field.tsx
import React, { useState, useEffect, FocusEvent } from "react";
import { useTranslation } from "react-i18next";
import { fromDecimalToPercent, fromPercentToDecimal, decimalToPercentString, percentToDecimalString } from "@/utils/percent";

interface ProjectInputFieldProps {
  label: string;
  name: string;
  value: string | number | boolean | null | undefined
  type?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  options?: string[];
  optionLabelKeyPrefix?: string;
}

export function ProjectInputField({
  label,
  name,
  value,
  type = "string",
  onChange,
  options,
  optionLabelKeyPrefix,
}: ProjectInputFieldProps) {
  const fieldClass = "project-form-field w-full pl-2 pr-6 py-1 border border-gray-300 rounded text-left text-xs";
  const isPercentage = type === "percentage";
  const isNumericType = type === "number" || type === "integer" || type === "percentage";
  const { t } = useTranslation();

  // If options are provided, render a select (no free text)
  if (options && options.length > 0) {
    const toLabel = (s: string) => {
      const t = s.replace(/_/g, " ").toLowerCase();
      return t.charAt(0).toUpperCase() + t.slice(1);
    };

    // Normalize current value to one of the option values (case-insensitive)
    const raw = value == null ? "" : String(value);
    const exact = options.find((o) => o === raw);
    const ciMatch = options.find((o) => o.toLowerCase() === raw.toLowerCase());
    const selectedValue = exact ?? ciMatch ?? "";

    return (
      <div className="flex items-center gap-2">
        <label htmlFor={name} className="w-80 text-gray-700 font-medium text-xs">
          {label}
        </label>
        <div className="relative w-48">
          <select
            id={name}
            name={name}
            value={selectedValue}
            onChange={(e) =>
              onChange({
                target: { name, value: e.target.value || null },
              } as unknown as React.ChangeEvent<HTMLInputElement>)
            }
            className={`${fieldClass} bg-white`}
          >
            {selectedValue === "" && (
              <option value="" disabled hidden>
                {t("forms.select_placeholder")}
              </option>
            )}
            {options.map((opt) => (
              <option key={opt} value={opt}>
                {optionLabelKeyPrefix
                  ? t(`${optionLabelKeyPrefix}.${opt}`, { defaultValue: toLabel(opt) })
                  : toLabel(opt)}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  }


  const isDate = type === "date";
  const isMonth = type === "month";


  if (isDate) {
    const val = typeof value === "string" ? value : "";

    return (
      <div className="flex items-center gap-2">
        <label htmlFor={name} className="w-80 text-gray-700 font-medium text-xs">
          {label}
        </label>
        <div className="relative w-48">
          <input
            id={name}
            name={name}
            type="date"
            value={val}
            placeholder={t("forms.date_placeholder")}
            onChange={(e) =>
              onChange({
                target: { name, value: e.target.value || null },
              } as unknown as React.ChangeEvent<HTMLInputElement>)
            }
            className={`${fieldClass} pr-6`}
          />
        </div>
      </div>
    );
  }

  if (isMonth) {
    // Accept both "YYYY-MM" and "YYYY-MM-DD" (show only YYYY-MM)
    let val = typeof value === "string" ? value : "";
    if (val.length >= 7) val = val.slice(0, 7); // normalize to YYYY-MM for the control

    return (
      <div className="flex items-center gap-2">
        <label htmlFor={name} className="w-80 text-gray-700 font-medium text-xs">
          {label}
        </label>
        <div className="relative w-48">
          <input
            id={name}
            name={name}
            type="month"
            value={val}
            placeholder={t("forms.month_placeholder")}
            onChange={(e) =>
              onChange({
                target: { name, value: e.target.value || null },
              } as unknown as React.ChangeEvent<HTMLInputElement>)
            }
            className={`${fieldClass} pr-6`}
          />
        </div>
      </div>
    );
  }



  const [inputValue, setInputValue] = useState<string>("");


  useEffect(() => {
    if (value === null || value === undefined) {
      setInputValue("");
      return;
    }
    if (isPercentage && typeof value === "number") {
      // display as human-friendly percent
      //setInputValue(fromDecimalToPercent(value).toString());
      setInputValue(decimalToPercentString(value));
      return;
    }
    // display as-is
    setInputValue(String(value));
  }, [value, isPercentage]);

  // treat near-equal numbers as equal (avoids dirty on rounding noise)
  const numEqual = (a: number, b: number) =>
    Math.abs(a - b) <= Math.max(1e-9, Math.abs(a) * 1e-9, Math.abs(b) * 1e-9);



  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
  const raw = e.target.value;
  const trimmed = raw.trim();

  if (isPercentage) {
    // empty -> null, but only if it actually changes
    if (trimmed === "") {
      if (value == null) return; // no change
      onChange({ target: { name, value: null } } as unknown as React.ChangeEvent<HTMLInputElement>);
      return;
    }
    const numeric = Number(trimmed.replace(",", "."));
    if (Number.isNaN(numeric)) return;
    // use string-safe conversion
    const decimalString = percentToDecimalString(trimmed);
    const decimal = Number(decimalString);
    if (typeof value === "number" && numEqual(value, decimal)) return; // no logical change
    onChange({ target: { name, value: decimal } } as unknown as React.ChangeEvent<HTMLInputElement>);
    return;
  }

  if (isNumericType) {
    // number / integer: empty -> null (only if changed); else numeric with equality guard
    if (trimmed === "") {
      if (value == null) return; // already null/undefined
      onChange({ target: { name, value: null } } as unknown as React.ChangeEvent<HTMLInputElement>);
      return;
    }
    const num = Number(trimmed.replace(",", "."));
    if (Number.isNaN(num)) return;
    const out = type === "integer" ? Math.trunc(num) : num;
    if (typeof value === "number") {
      if (type === "integer" ? value === out : numEqual(value, out)) return; // no change
    }
    onChange({ target: { name, value: out } } as unknown as React.ChangeEvent<HTMLInputElement>);
    return;
  }

  // string: only propagate if different after trim
  const current = value == null ? "" : String(value);
  if (trimmed === current) return; // no change
  onChange({ target: { name, value: trimmed } } as unknown as React.ChangeEvent<HTMLInputElement>);
  
};


  return (
    <div className="flex items-center gap-2">
      <label htmlFor={name} className="w-96 text-gray-700 font-medium text-xs">
        {label}
      </label>
      <div className="relative w-56">
        <input
          id={name}
          name={name}
          type="text"
          inputMode={isNumericType ? "decimal" : "text"}
          value={inputValue}
          onChange={(e) => {
            const raw = e.target.value;
            setInputValue(raw);
            // Forward keystrokes so the store marks the field dirty immediately. We intentionally pass the raw string here (for number/percent too). 
            // On blur, handleBlur will convert to decimal/number and send the final value.
            onChange({
              target: { name, value: raw },
            } as unknown as React.ChangeEvent<HTMLInputElement>);
          }}
          onBlur={handleBlur}
          className={`${fieldClass} pr-6`}
        />
        {isPercentage && (
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500 pointer-events-none">
            %
          </span>
        )}
      </div>
    </div>
  );
}